import { useContext, useEffect, useRef } from 'react';
import { SocketContext } from '@streamster/shared/socket/socket.provider';
import { ChannelState, DeviceState, TargetMode, AutoLoginState } from '@streamster/shared/socket/document.type';

/**
 * Shares what the user's app is doing right now with the support assistant, so it
 * can diagnose streaming problems instead of asking them to describe their setup.
 *
 * None of this exists in any database — channels, encoder settings and device
 * health live only in the websocket document — so the app is the only thing that
 * can report it. That makes it unverified user-reported data: the chat backend
 * (api/_lib/clientContext.ts) validates every value against a fixed vocabulary and
 * fences it in the prompt. The strings produced here MUST match that vocabulary,
 * or the field is silently dropped on arrival.
 *
 * Credentials are stripped here, at the source, and must never be added: stream
 * keys, RTMP URLs and VPN passwords all sit in the same document. Whether a key is
 * set is the most we ever say about one.
 */

// Pushed on a timer rather than on every change: the document ticks constantly
// (bitrate, FPS) and the widget only reads this when a message is actually sent.
// The interval also covers the widget script loading after we do.
const PUSH_INTERVAL_MS = 5000;

const DEVICE_TYPES: Record<string, string> = {
  win: 'windows',
  and: 'android',
  ios: 'ios',
  web: 'web',
  ext: 'external',
};

const DEVICE_STATES: Record<number, string> = {
  [DeviceState.Inactive]: 'inactive',
  [DeviceState.Offline]: 'offline',
  [DeviceState.Online]: 'online',
};

const CHANNEL_STATES: Record<number, string> = {
  [ChannelState.Idle]: 'idle',
  [ChannelState.RunningOk]: 'ok',
  [ChannelState.RunningInitError]: 'init error',
  [ChannelState.RunningConnectError]: 'connect error',
};

const TARGET_MODES: Record<number, string> = {
  [TargetMode.ManualKey]: 'manual key',
  [TargetMode.AutoLogin]: 'auto login',
};

const AUTO_LOGIN_STATES: Record<number, string> = {
  [AutoLoginState.Unknown]: 'unknown',
  [AutoLoginState.InProgress]: 'in progress',
  [AutoLoginState.Authenticated]: 'authenticated',
  [AutoLoginState.NotAuthenticated]: 'not authenticated',
  [AutoLoginState.KeyObtained]: 'key obtained',
  [AutoLoginState.KeyNotFound]: 'key not found',
};

// EncoderType and VpnState aren't exported from document.type, so map by ordinal.
const ENCODER_TYPES = ['auto', 'hardware', 'software'];
const VPN_STATES = ['idle', 'connecting', 'reconnecting', 'connected'];

const round = (value: unknown) => {
  const n = Number(value);

  return Number.isFinite(n) ? Math.round(n) : undefined;
};

const useSupportChatContext = () => {
  const { serverData, commonData } = useContext(SocketContext) ?? ({} as any);
  // Read through a ref so the interval always sees the latest document without
  // being torn down and rebuilt on every socket tick.
  const snapshotRef = useRef<unknown>(null);

  snapshotRef.current = (() => {
    if (!serverData?.Channels && !serverData?.Devices) {
      return null;
    }

    const device = serverData?.Devices?.[commonData?.selectedSceneOwner];
    const kpis = commonData?.KPIs ?? device?.KPIs;
    const settings = serverData?.Settings;

    const channels = Object.values(serverData?.Channels ?? {})
      .filter(Boolean)
      .map((channel: any) => ({
        // The platform, not the user's own channel label — more useful for
        // diagnosis and it keeps their free text out of the prompt.
        platform: serverData?.Targets?.[channel.TargetId]?.Name ?? 'custom RTMP',
        is_on: Boolean(channel.IsOn),
        state: CHANNEL_STATES[channel.State],
        bitrate: round(channel.Bitrate),
        // Never the key itself — only whether one is present.
        has_key: Boolean(channel.Key),
        target_mode: TARGET_MODES[channel.TargetMode],
        auto_login_state: AUTO_LOGIN_STATES[channel.AutoLoginState],
      }));

    return {
      device: device
        ? {
            type: DEVICE_TYPES[device.Type] ?? DEVICE_TYPES[commonData?.currentDeviceType],
            state: DEVICE_STATES[device.State],
            sleeping: Boolean(device.Sleeping),
            cpu_load: round(kpis?.Cpu?.Load),
            encoder_fps: round(kpis?.Encoder?.InputFps),
            encoder_target_fps: round(kpis?.Encoder?.InputTargetFps),
            cloud_out_bitrate: round(kpis?.CloudOut?.Bitrate),
            cloud_out_errors: round(kpis?.CloudOut?.Errors),
            cloud_out_drops: round(kpis?.CloudOut?.Drops),
            vpn_state: VPN_STATES[device.VpnState],
          }
        : undefined,
      settings: settings
        ? {
            bitrate: round(settings.Bitrate),
            fps: round(settings.Fps),
            resolution:
              settings.Resolution?.Width && settings.Resolution?.Height
                ? `${settings.Resolution.Width}x${settings.Resolution.Height}`
                : undefined,
            encoder_type: ENCODER_TYPES[settings.EncoderType],
            no_stream_without_vpn: Boolean(settings.NoStreamWithoutVpn),
            // Numeric TargetPreference (None=0, Unknown=1, Adult=2, Vlog=3),
            // passed through as-is. This is the same value that decides whether
            // the CamGems menu item is shown (see WebNavigation.tsx); support
            // chat uses it to decide whether adult-streaming help applies to
            // this user, so the two stay consistent by construction.
            target_preference: settings.TargetPreference,
          }
        : undefined,
      channels,
    };
  })();

  useEffect(() => {
    let lastPushed: string | null = null;

    const push = () => {
      const setContext = (window as any)?.SupportChat?.setContext;
      if (typeof setContext !== 'function') {
        return;
      }

      const snapshot = snapshotRef.current;
      const serialized = snapshot ? JSON.stringify(snapshot) : null;
      if (serialized === lastPushed) {
        return;
      }

      lastPushed = serialized;
      setContext(snapshot);
    };

    push();
    const timer = setInterval(push, PUSH_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);
};

export default useSupportChatContext;
